// useChatAnalyserApis.js - WebSocket Implementation for Analyzer
import { useState, useCallback, useRef, useEffect } from 'react';
import { useUser } from '../../auth/hooks/useUser';
import { analyzerStreamService } from '../../../shared/workers/ChatStreamService';
import logger from '../../../shared/services/logging/logger';
import { MAX_PENDING_MESSAGES } from '../config/websocketConfig';

// ── Thin wrappers kept for backward compatibility ───────────────
// Components that imported these directly (BackgroundChatListener, etc.)
// can continue to do so without changes.

export const initializeWebSocket = () => {
  analyzerStreamService.connect();
};

export const closeWebSocket = () => {
  analyzerStreamService.disconnect();
};

export const disconnectFromWebSocket = () => {
  analyzerStreamService.disconnect();
};

export const isWebSocketConnected = () => {
  return analyzerStreamService.isRunning();
};

export const updateActivity = () => {
  // No-op — the worker-based service does not need activity tracking
};

export const registerMessageHandler = (handler) => {
  return analyzerStreamService.registerHandler(handler);
};

// ── Hook ────────────────────────────────────────────────────────

/**
 * React hook for managing chat analyzer WebSocket communication.
 * Provides methods for sending messages, stopping streams, and terminating conversations.
 *
 * @param {string} conversationId - The active conversation ID to scope messages to
 */
const useChatAnalyserApi = (conversationId) => {
  const { userData } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const streamCallbackRef = useRef(null);
  const currentChatIdRef = useRef(null);
  const bufferedMessagesRef = useRef([]);

  // Register a message handler for the lifetime of this hook instance
  useEffect(() => {
    const handleMessage = (data) => {
      if (data.chatId) {
        currentChatIdRef.current = data.chatId;
      }

      if (!streamCallbackRef.current) {
        // Buffer stream events so they are replayed when reattachStream is called.
        // Stop events are excluded — replaying a stop on reconnect would abort the resumed stream.
        if (!data.stopped && data.type !== 'stop_confirmed') {
          if (bufferedMessagesRef.current.length < MAX_PENDING_MESSAGES) {
            bufferedMessagesRef.current.push(data);
          }
        }
        return;
      }

      if (data.type === 'stop_confirmed' || data.stopped) {
        logger.info('Stop confirmed by Analyzer backend');
        streamCallbackRef.current({
          type: 'complete',
          content: '',
          stopped: true,
          chatId: data.chatId
        });
        streamCallbackRef.current = null;
        currentChatIdRef.current = null;
        bufferedMessagesRef.current = [];
        return;
      }

      streamCallbackRef.current(data);
    };

    const unregister = analyzerStreamService.registerHandler(handleMessage);
    return unregister;
  }, []);

  // Pre-seed session identity so the service has conversationId/userId/tenantId
  // available before WS_OPEN fires — required for page-reload resumption to work.
  useEffect(() => {
    if (conversationId && userData?.userId) {
      analyzerStreamService.setSession({
        userId: userData.userId,
        conversationId,
        tenantId: userData.organizationId || '',
      });
    }
  }, [conversationId, userData?.userId, userData?.organizationId]);

  const sendChatMessage = useCallback(async (messageData, onStreamCallback) => {
    if (!conversationId || !userData?.userId) {
      setError('Missing conversation ID or user authentication');
      return;
    }

    setLoading(true);
    setError(null);
    streamCallbackRef.current = onStreamCallback;
    currentChatIdRef.current = null;
    bufferedMessagesRef.current = [];

    try {
      const message = {
        action: 'publishEvent',
        message: messageData.message,
        conversationId,
        userId: userData.userId,
        tenantId: userData.organizationId || '',
        s3_bucket_name: userData.conversationsBucket || ''
      };

      if (messageData.s3FilesData?.length > 0) {
        message.files = messageData.s3FilesData;
      }

      analyzerStreamService.sendMessage(message);
      logger.info('Analyzer message sent');

    } catch (err) {
      logger.error('Failed to send Analyzer message', { error: err.message });
      setError(err.message);
      streamCallbackRef.current = null;
      throw err;
    } finally {
      setLoading(false);
    }
  }, [conversationId, userData]);

  const stopMessage = useCallback(async (chatId = null) => {
    const chatIdToStop = chatId || currentChatIdRef.current;
    logger.info('stopMessage called', { chatId: chatIdToStop });

    try {
      analyzerStreamService.sendMessage({
        action: 'terminateEvent',
        tenantId: userData?.organizationId || '',
        userId: userData?.userId || '',
        conversationId,
        chatId: chatIdToStop
      });
    } catch (err) {
      logger.error('Failed to send terminateEvent', { error: err.message });
    }

    if (streamCallbackRef.current) {
      streamCallbackRef.current({ type: 'complete', content: '', stopped: true, chatId: chatIdToStop });
    }
    streamCallbackRef.current = null;
    currentChatIdRef.current = null;
    return true;
  }, [conversationId, userData]);

  const terminateConversation = useCallback(async (chatId) => {
    if (!conversationId || !userData?.userId) return false;
    try {
      analyzerStreamService.sendMessage({
        action: 'terminateEvent',
        userId: userData.userId,
        chatId,
        conversationId
      });
      return true;
    } catch (err) {
      logger.error('Failed to terminate conversation', { error: err.message });
      return false;
    }
  }, [conversationId, userData]);

  const forceNewConnection = useCallback(() => {
    logger.info('Forcing new Analyzer stream connection');
    analyzerStreamService.disconnect();
    analyzerStreamService.connect();
  }, []);

  const reattachStream = useCallback((onStreamCallback) => {
    logger.info('Reattaching to background analyzer stream', { conversationId });
    streamCallbackRef.current = onStreamCallback;

    const buffered = [...bufferedMessagesRef.current];
    bufferedMessagesRef.current = [];
    if (buffered.length > 0) {
      logger.info('Replaying buffered messages after reattach', { count: buffered.length });
      buffered.forEach(data => {
        try { onStreamCallback(data); } catch (e) {
          logger.error('Error replaying buffered message', { error: e.message });
        }
      });
    }
  }, [conversationId]);

  const resetSequenceForFullReplay = useCallback(() => {
    analyzerStreamService.resetSequenceForFullReplay();
  }, []);

  const retrySubscription = useCallback(() => {
    return analyzerStreamService.retrySubscription();
  }, []);

  const isStreamActive = useCallback(() => {
    return analyzerStreamService.isStreamActive();
  }, []);

  return {
    sendChatMessage,
    stopMessage,
    terminateConversation,
    forceNewConnection,
    reattachStream,
    resetSequenceForFullReplay,
    retrySubscription,
    isStreamActive,
    loading,
    error
  };
};

export default useChatAnalyserApi;
